import type { Passkey, SessionInfo, User } from "../types/models";
import { apiData, apiPath, resolveApiUrl, setCsrfToken, type Schema } from "./api";
import { getProfile } from "./profile";
import { createPasskeyCredential, getPasskeyAssertion } from "../lib/webauthn";

export type GetCurrentUserOptions = {
  requireProfile?: boolean;
};

export async function getCurrentUser(options: GetCurrentUserOptions = {}): Promise<User | null> {
  const data = await apiData<Schema<"UserContextResponseEnvelope">>("/auth/me");
  const user = toUser(data);
  if (!user) {
    return null;
  }
  try {
    const profile = await getProfile();
    return profile ? { ...user, ...profile } : user;
  } catch (error) {
    if (options.requireProfile) {
      throw error;
    }
    return user;
  }
}

export async function login(body: Schema<"LoginRequest">): Promise<User | null> {
  const data = await apiData<Schema<"LoginResponseEnvelope">, Schema<"LoginRequest">>("/auth/login", { method: "POST", body });
  setCsrfToken(data.csrf_token ?? "");
  return getCurrentUser();
}

export async function register(body: Schema<"RegisterRequest">): Promise<User | null> {
  await apiData<Schema<"RegisterResponseEnvelope">, Schema<"RegisterRequest">>("/auth/register", { method: "POST", body });
  try {
    return await getCurrentUser();
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>("/auth/logout", { method: "POST" });
  setCsrfToken("");
}

export async function logoutAll(): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>("/auth/logout-all", { method: "POST" });
  setCsrfToken("");
}

export async function revokeSession(id: string): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>(apiPath(`/auth/sessions/${id}`), { method: "DELETE" });
}

export async function deleteOwnAccount(): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>("/me", { method: "DELETE" });
  setCsrfToken("");
}

export async function requestPasswordReset(body: Schema<"PasswordResetRequest">): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"PasswordResetRequest">>("/auth/password/reset/request", { method: "POST", body });
}

export async function confirmPasswordReset(body: Schema<"PasswordResetConfirmRequest">): Promise<void> {
  const { token, ...safeBody } = body;
  const path = token ? apiPath(`/auth/password/reset/confirm/${encodeURIComponent(token)}`) : "/auth/password/reset/confirm";
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"PasswordResetConfirmRequest">>(path, { method: "POST", body: safeBody });
}

export async function changePassword(body: Schema<"ChangePasswordRequest">): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"ChangePasswordRequest">>("/auth/password/change", { method: "POST", body });
}

export async function listSessions(): Promise<SessionInfo[]> {
  const data = await apiData<Schema<"SessionResponseListEnvelope">>("/auth/sessions", { query: { limit: 50, offset: 0 } });
  return data.filter(isActiveSession);
}

export async function setupTotp(): Promise<Schema<"TOTPSetupResponse">> {
  return apiData<Schema<"TOTPSetupResponseEnvelope">>("/auth/totp/setup", { method: "POST" });
}

export async function verifyTotp(body: Schema<"TOTPVerifyRequest">): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"TOTPVerifyRequest">>("/auth/totp/verify", { method: "POST", body });
}

export async function generateRecoveryCodes(): Promise<string[]> {
  const data = await apiData<Schema<"RecoveryCodesResponseEnvelope">>("/auth/recovery-codes", { method: "POST" });
  return data.recovery_codes ?? [];
}

export async function listPasskeys(): Promise<Passkey[]> {
  const data = await apiData<Schema<"PasskeyResponseListEnvelope">>("/auth/passkeys", { query: { limit: 50, offset: 0 } });
  return data.filter(hasId);
}

export async function registerPasskeyWithBrowser(friendlyName?: string): Promise<void> {
  const options = await apiData<Schema<"PasskeyCreationOptionsResponseEnvelope">>("/auth/passkeys/register/begin", { method: "POST" });
  const credential = await createPasskeyCredential(options.publicKey);
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"PasskeyRegisterRequest">>("/auth/passkeys/register/finish", {
    method: "POST",
    body: { credential, friendly_name: friendlyName || undefined }
  });
}

export async function loginWithPasskey(): Promise<User | null> {
  const options = await apiData<Schema<"PasskeyRequestOptionsResponseEnvelope">>("/auth/passkeys/login/begin", { method: "POST" });
  const credential = await getPasskeyAssertion(options.publicKey);
  const data = await apiData<Schema<"LoginResponseEnvelope">, Schema<"PasskeyLoginFinishRequest">>("/auth/passkeys/login/finish", {
    method: "POST",
    body: { credential }
  });
  setCsrfToken(data.csrf_token ?? "");
  return getCurrentUser();
}

export async function deletePasskey(id: string): Promise<void> {
  await apiData<Schema<"EmptyResponseEnvelope">>(apiPath(`/auth/passkeys/${id}`), { method: "DELETE" });
}

export function getOidcStartUrl(provider: "google" | "apple"): string {
  return resolveApiUrl(`/auth/oidc/${provider}/start`);
}

export async function validateInvitation(token: string): Promise<Schema<"InvitationValidationResponse">> {
  return apiData<Schema<"InvitationValidationResponseEnvelope">>(apiPath(`/invitations/${encodeURIComponent(token)}`));
}

export async function completeInvitation(token: string, body: Omit<Schema<"InvitationCompleteRequest">, "token">): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"InvitationCompleteRequest">>(apiPath(`/invitations/${encodeURIComponent(token)}/complete`), {
    method: "POST",
    body
  });
}

function toUser(data: Schema<"UserContextResponse"> | Schema<"LoginResponse">["user"] | undefined): User | null {
  if (!data?.id) {
    return null;
  }
  return data as User;
}

function hasId<T extends { id?: string }>(item: T): item is T & { id: string } {
  return Boolean(item.id);
}

function isActiveSession<T extends { id?: string; revoked_at?: string | null }>(item: T): item is T & { id: string } {
  return hasId(item) && !item.revoked_at;
}
